import { getAuthToken } from './auth';

// const BASE_URL = 'http://192.168.0.175/campus_marketplace/backend/api';
// const IMAGE_BASE_URL = 'http://192.168.0.175/campus_marketplace/backend';
const BASE_URL = 'https://api.ecomart.ng';
const IMAGE_BASE_URL = 'https://api.ecomart.ng';

// ============================================
// PRODUCTION CACHE SERVICE
// ============================================

class ProductionCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 20 * 60 * 1000; // 20 minutes
    this.stats = {
      hits: 0,
      misses: 0,
      clears: 0,
      totalSize: 0
    };

    // Setup automatic cleanup every 10 minutes
    this.cleanupInterval = setInterval(() => this.cleanupExpired(), 10 * 60 * 1000);
    
    // Limit cache size to prevent memory issues
    this.maxCacheSize = 500;
  }

  generateKey(endpoint, params = {}) {
    try {
      const paramString = Object.keys(params)
        .sort()
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(params[key]))}`)
        .join('&');
      
      return `${endpoint}${paramString ? `?${paramString}` : ''}`;
    } catch (error) {
      // Fallback to simple key if serialization fails
      return `${endpoint}_${JSON.stringify(params)}`;
    }
  }

  set(key, data, ttl = this.defaultTTL) {
    // Check cache size limit
    if (this.cache.size >= this.maxCacheSize) {
      this.evictOldest();
    }

    const expiry = Date.now() + ttl;
    const size = this.calculateSize(data);
    
    this.cache.set(key, {
      data,
      expiry,
      timestamp: Date.now(),
      size
    });
    
    this.stats.totalSize += size;
    return true;
  }

  get(key) {
    const item = this.cache.get(key);
    
    if (!item) {
      this.stats.misses++;
      return null;
    }

    // Check if cache has expired
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      this.stats.totalSize -= item.size;
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return item.data;
  }

  delete(key) {
    const item = this.cache.get(key);
    if (item) {
      this.stats.totalSize -= item.size;
    }
    return this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
    this.stats.totalSize = 0;
    this.stats.clears++;
  }

  clearByPattern(pattern) {
    let cleared = 0;
    for (const [key, value] of this.cache.entries()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.stats.totalSize -= value.size;
        cleared++;
      }
    }
    return cleared;
  }

  cleanupExpired() {
    const now = Date.now();
    let expiredCount = 0;
    
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiry) {
        this.cache.delete(key);
        this.stats.totalSize -= value.size;
        expiredCount++;
      }
    }
    
    if (expiredCount > 0 && process.env.NODE_ENV === 'development') {
      // console.log(`🧹 Cleaned up ${expiredCount} expired cache entries`);
    }
  }

  evictOldest() {
    let oldestKey = null;
    let oldestTime = Date.now();
    
    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      const item = this.cache.get(oldestKey);
      this.cache.delete(oldestKey);
      this.stats.totalSize -= item.size;
    }
  }

  calculateSize(data) {
    try {
      const jsonString = JSON.stringify(data);
      return new Blob([jsonString]).size;
    } catch {
      return 1024; // Default 1KB if can't calculate
    }
  }

  getStats() {
    const now = Date.now();
    let active = 0;
    let expired = 0;
    
    for (const item of this.cache.values()) {
      if (now > item.expiry) {
        expired++;
      } else {
        active++;
      }
    }
    
    return {
      total: this.cache.size,
      active,
      expired,
      hits: this.stats.hits,
      misses: this.stats.misses,
      clears: this.stats.clears,
      totalSize: this.formatBytes(this.stats.totalSize),
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0
    };
  }

  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  destroy() {
    clearInterval(this.cleanupInterval);
    this.clear();
  }
}

// Create singleton cache instance
const apiCache = new ProductionCache();

// ============================================
// ENHANCED FETCH WITH RETRY LOGIC
// ============================================

const fetchWithRetry = async (url, options = {}, retries = 3, backoff = 300) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      
      // If rate limited, wait and retry
      if (response.status === 429) {
        const waitTime = response.headers.get('Retry-After') || backoff * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, backoff * Math.pow(2, i)));
    }
  }
};

// ============================================
// CACHED FETCH WRAPPER
// ============================================

const cachedFetch = async (endpoint, params = {}, options = {}) => {
  const {
    method = 'GET',
    body,
    forceRefresh = false,
    ttl = 20 * 60 * 1000,
    customKey = null,
    useRetry = true,
    retries = 2
  } = options;

  // Only cache GET requests
  const shouldCache = method === 'GET' && !forceRefresh;
  
  // Generate cache key
  const cacheKey = customKey || apiCache.generateKey(endpoint, params);
  
  // Try to get from cache
  if (shouldCache) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData !== null) {
      return cachedData;
    }
  }

  // Build URL with query params
  let url = `${BASE_URL}${endpoint}`;
  if (method === 'GET' && params && Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    url += `?${queryParams.toString()}`;
  }

  try {
    // Fetch from API with optional retry
    const fetchOptions = {
      method,
      headers: getAuthHeaders(),
      ...(body && method !== 'GET' && { body: JSON.stringify(body) })
    };

    const response = useRetry 
      ? await fetchWithRetry(url, fetchOptions, retries)
      : await fetch(url, fetchOptions);
    
    const data = await handleResponse(response);
    
    // Cache successful GET responses
    if (shouldCache && data && !data.error) {
      try {
        apiCache.set(cacheKey, data, ttl);
      } catch (cacheError) {
        // console.warn('Cache set failed:', cacheError.message);
      }
    }
    
    return data;
  } catch (error) {
    // Return cached data as fallback if available
    if (shouldCache && !forceRefresh) {
      const cachedData = apiCache.get(cacheKey);
      if (cachedData !== null) {
        // console.warn(`Using cached data due to fetch error: ${error.message}`);
        return { ...cachedData, _cached: true, _error: error.message };
      }
    }
    throw error;
  }
};

// ============================================
// AUTH HEADERS & RESPONSE HANDLER
// ============================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

const handleResponse = async (response) => {
  let data;
  
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  
  // Check if this is a payment verification endpoint
  const isPaymentVerification = response.url.includes('/payment/status');
  
  if (!response.ok) {
    // For payment verification, don't throw - just return the data
    if (isPaymentVerification) {
      return {
        success: false,
        status: data.status || 'unknown',
        error: data.error || 'Payment verification failed',
        originalStatus: response.status
      };
    }
    
    // Handle authentication errors
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not on auth pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        window.location.href = '/login?session=expired';
      }
    }
    
    // Handle 409 Conflict specially
    if (response.status === 409) {
      return {
        success: false,
        message: data.message || 'Conflict occurred',
        status: 409,
        data: null
      };
    }
    
    // Handle verification needed
    if (data.needs_verification) {
      throw {
        needsVerification: true,
        userId: data.user_id,
        email: data.email,
        message: data.message || 'Please verify your email first'
      };
    }
    
    // Handle rate limiting
    if (response.status === 429) {
      throw {
        rateLimited: true,
        retryAfter: data.retry_after,
        message: data.error || 'Too many requests. Please try again later.'
      };
    }
    
    throw new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  return data;
};

// ============================================
// CORE API FUNCTIONS WITH CACHING
// ============================================

// ---------- CATEGORIES ----------
export const getCategories = async (options = {}) => {
  return cachedFetch('/categories', {}, { 
    ttl: 30 * 60 * 1000, // 30 minutes
    ...options 
  });
};

// ---------- LISTINGS ----------
export const getListings = async (filters = {}, options = {}) => {
  return cachedFetch('/listings', filters, { 
    ttl: 5 * 60 * 1000, // 5 minutes
    ...options 
  });
};

export const getListing = async (listingId, options = {}) => {
  return cachedFetch(`/listings/${listingId}`, {}, { 
    ttl: 10 * 60 * 1000, // 10 minutes
    ...options 
  });
};

export const getListingRatings = async (listingId, options = {}) => {
  return cachedFetch(`/listings/${listingId}/ratings`, {}, { 
    ttl: 10 * 60 * 1000,
    ...options 
  });
};

export const getListingPerformance = async (listingId, options = {}) => {
  return cachedFetch(`/listings/${listingId}/performance`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getPerformanceComparison = async (listingId, timeRange = '7d', options = {}) => {
  return cachedFetch(`/listings/${listingId}/performance-comparison?range=${timeRange}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const reportListing = async (reportData) => {
  const response = await fetch(`${BASE_URL}/listings/${reportData.listing_id}/report`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(reportData)
  });
  
  return handleResponse(response);
};

export const reportSeller = async (reportData) => {
  const response = await fetch(
    `${BASE_URL}/sellers/${reportData.seller_id}/report`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reportData)
    }
  );

  return handleResponse(response);
};


// ---------- FAVORITES ----------
export const getFavorites = async (userId, options = {}) => {
  return cachedFetch(`/favorites/${userId}`, {}, { 
    ttl: 10 * 60 * 1000,
    ...options 
  });
};

export const checkIsFavorite = async (userId, listingId, options = {}) => {
  const params = { user_id: userId, listing_id: listingId };
  return cachedFetch('/favorites/check', params, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- NOTIFICATIONS ----------
export const getUserNotifications = async (options = {}) => {
  const userStr = localStorage.getItem('user');
  let userId = null;
  
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
    } catch {
      // console.error('Error parsing user data');
    }
  }
   
  const params = { };
  return cachedFetch('/notifications', params, { 
    ttl: 2 * 60 * 1000, // 2 minutes
    ...options 
  });
};

export const getUnreadCountNotifications = async (options = {}) => {
  return cachedFetch('/notifications/unread-count', { }, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

// ---------- USERS ----------
export const getUser = async (userId, options = {}) => {
  return cachedFetch(`/users/${userId}`, {}, { 
    ttl: 10 * 60 * 1000,
    ...options 
  });
};

export const updateUser = async (userId, userData) => {
  const data = await cachedFetch(`/users/${userId}`, {}, {
    method: 'PUT',
    body: userData,
    forceRefresh: true
  });
  
  // Invalidate user cache
  apiCache.delete(apiCache.generateKey(`/users/${userId}`, {}));
  
  return data;
};

export const getStates = async (options = {}) => {
  return cachedFetch('/users/states', {}, { 
    ttl: 24 * 60 * 60 * 1000, // 24 hours
    ...options 
  });
};

export const getLgasByState = async (stateId, options = {}) => {
  return cachedFetch(`/users/states/${stateId}/lgas`, {}, { 
    ttl: 24 * 60 * 60 * 1000,
    ...options 
  });
};

export const getLcdasByLga = async (lgaId, options = {}) => {
  return cachedFetch(`/users/lgas/${lgaId}/lcdas`, {}, { 
    ttl: 24 * 60 * 60 * 1000,
    ...options 
  });
};

// ---------- SELLER ----------
export const getSellerProfile = async (sellerId, options = {}) => {
  return cachedFetch(`/sellers/${sellerId}`, {}, { 
    ttl: 15 * 60 * 1000,
    ...options 
  });
};

export const getSellerStats = async (sellerId, options = {}) => {
  return cachedFetch(`/sellers/${sellerId}/stats`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- FIXED NON-CACHEABLE POST ENDPOINTS ----------

export const getTotalUnreadCount = async (userId) => {
  const response = await fetch(`${BASE_URL}/messages/total-unread`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ user_id: userId }),
  });
  return handleResponse(response);
};

export const getSellerUnreadCount = async (sellerId) => {
  const response = await fetch(`${BASE_URL}/messages/seller-unread`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ seller_id: sellerId }),
  });
  return handleResponse(response);
};

export const getUnreadCountsBySeller = async (sellerId) => {
  const response = await fetch(`${BASE_URL}/messages/seller-order-unread`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ seller_id: sellerId }),
  });
  return handleResponse(response);
};

// Also remove the cached versions of these functions from the CORE API FUNCTIONS section

export const getSellerAllRatings = async (sellerId, limit = 20, offset = 0, options = {}) => {
  const params = { limit, offset };
  return cachedFetch(`/ratings/seller/${sellerId}`, params, { 
    ttl: 10 * 60 * 1000,
    ...options 
  });
};

// ---------- ANALYTICS ----------
export const getSellerAnalytics = async (userId, timeframe = '30d', options = {}) => {
  const params = { timeframe };
  return cachedFetch(`/sellers/${userId}/analytics`, params, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- ORDERS ----------
export const getOrdersByBuyer = async (userId, options = {}) => {
  return cachedFetch(`/orders/buyer/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getOrdersBySeller = async (userId, options = {}) => {
  return cachedFetch(`/orders/seller/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getOrder = async (orderId, options = {}) => {
  return cachedFetch(`/orders/${orderId}`, {}, { 
    ttl: 10 * 60 * 1000,
    ...options 
  });
};

// ---------- WALLET ----------
export const getWalletBalance = async ( options = {}) => {
  const params = { };
  return cachedFetch('/wallet/balance', params, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getWalletDetails = async ( options = {}) => {
  const params = { };
  return cachedFetch('/wallet/details', params, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getTransactionHistory = async (limit = 150, offset = 0, options = {}) => {
  const params = { limit, offset };
  return cachedFetch('/wallet/transactions', params, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getTransactionById = async (transactionId, userId) => {
  const response = await fetch(`${BASE_URL}/wallet/transaction/${transactionId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId
    })
  });
  return handleResponse(response);
};

// ---------- PAYOUT METHODS ----------

export const getPayoutMethods = async () => {
  const response = await fetch(`${BASE_URL}/wallet/payout-methods`, {
    method: 'GET',
    headers: getAuthHeaders() // JWT contains user info
  });
 return handleResponse(response);
};


export const getNigerianBanks = async (options = {}) => {
  return cachedFetch('/banks', {}, { 
    ttl: 24 * 60 * 60 * 1000,
    ...options 
  });
};

// ---------- MY LISTINGS ----------
export const getMyListings = async (options = {}) => {
  return cachedFetch('/my-listings', {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- SUPPORT ----------
export const getUserSupportTickets = async (userId, status = null, options = {}) => {
  let params = { user_id: userId };
  if (status) params.status = status;
  
  return cachedFetch(`/support/tickets/user/${userId}`, params, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getSupportTicket = async (ticketId) => {
  const response = await fetch(`${BASE_URL}/support/tickets/${ticketId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getSupportCategories = async (options = {}) => {
  return cachedFetch('/support/categories', {}, { 
    ttl: 24 * 60 * 60 * 1000,
    ...options 
  });
};

export const getSupportStatistics = async (userId = null, options = {}) => {
  const url = userId ? `/support/statistics/${userId}` : '/support/statistics';
  return cachedFetch(url, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getSupportTicketsInvolved = async (userId, options = {}) => {
  return cachedFetch(`/support/tickets/involved/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- BOOST ----------
export const getBoostPackages = async (options = {}) => {
  return cachedFetch('/boost/packages', {}, { 
    ttl: 30 * 60 * 1000,
    ...options 
  });
};

export const getListingBoosts = async (listingId, options = {}) => {
  return cachedFetch(`/boost/listing/${listingId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getUserBoostHistory = async (userId, options = {}) => {
  return cachedFetch(`/boost/user-history/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getUserBoostPerformanceHistory = async (userId, options = {}) => {
  return cachedFetch(`/boost/user-performance/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getBoostPerformance = async (boostId, options = {}) => {
  return cachedFetch(`/boost/performance/${boostId}/comparison`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getBoostStatus = async (orderId, options = {}) => {
  return cachedFetch(`/boost/order/${orderId}/status`, {}, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getBoostTransactions = async (userId, options = {}) => {
  return cachedFetch(`/boost/transactions/${userId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- CONVERSATIONS ----------
export const getConversations = async (userId, options = {}) => {
  return cachedFetch(`/conversations/${userId}`, {}, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getMessages = async (conversationId) => {
  const response = await fetch(`${BASE_URL}/messages/${conversationId}`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

// ---------- DISPUTES ----------
export const getDisputeDetails = async (disputeId, options = {}) => {
  return cachedFetch(`/disputes/${disputeId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getDisputeEvidence = async (disputeId, options = {}) => {
  return cachedFetch(`/disputes/${disputeId}/evidence-list`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getUserDisputes = async (userId, options = {}) => {
  return cachedFetch(`/disputes/user/${userId}/all`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getDisputeMessages = async (disputeId, options = {}) => {
  return cachedFetch(`/disputes/${disputeId}/messages`, {}, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getDisputeCategories = async (raisedBy = null, options = {}) => {
  const params = raisedBy ? { raised_by: raisedBy } : {};
  return cachedFetch('/disputes/categories', params, { 
    ttl: 24 * 60 * 60 * 1000,
    ...options 
  });
};

// ---------- ESCROW ----------
export const getActiveConversationEscrow = async (conversationId, options = {}) => {
  return cachedFetch(`/chat/${conversationId}/active-escrow`, {}, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getConversationEscrowHistory = async (conversationId, options = {}) => {
  return cachedFetch(`/chat/${conversationId}/escrow-history`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getUserEscrowHistory = async ( options = {}) => {
  return cachedFetch('/user/escrow-history', { }, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

export const getEscrowStatus = async (escrowId, options = {}) => {
  return cachedFetch(`/escrow/${escrowId}/status`, {}, { 
    ttl: 2 * 60 * 1000,
    ...options 
  });
};

export const getEscrowDetails = async (escrowId, options = {}) => {
  return cachedFetch(`/escrow/${escrowId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ---------- TRACKING ----------
export const getTrackingAnalytics = async (listingId, options = {}) => {
  return cachedFetch(`/tracking/analytics/${listingId}`, {}, { 
    ttl: 5 * 60 * 1000,
    ...options 
  });
};

// ============================================
// NON-CACHEABLE FUNCTIONS (Write operations)
// ============================================

export const login = async (email, password) => {
  const response = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await handleResponse(response);
  
  // Store auth data
  localStorage.setItem('token', data.token);
  localStorage.setItem('user', JSON.stringify(data.user));
  
  // Clear user-specific cache on login
  if (data.user?.user_id) {
    const userId = data.user.user_id;
    apiCache.clearByPattern(`/favorites/${userId}`);
    apiCache.clearByPattern(`/users/${userId}`);
    apiCache.clearByPattern(`/notifications?user_id=${userId}`);
    apiCache.clearByPattern('/my-listings');
  }
  
  return {
    success: true,
    user: data.user,
    token: data.token,
    hasNotifications: data.has_notifications || false
  };
};

export const register = async (userData) => {
  const response = await fetch(`${BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });

  return handleResponse(response);
};

export const createListing = async (listingData) => {
  const data = await cachedFetch('/listings', {}, {
    method: 'POST',
    body: listingData,
    forceRefresh: true
  });
  
  // Invalidate relevant cache
  apiCache.clearByPattern('/listings');
  apiCache.clearByPattern('/my-listings');
  
  return data;
};

export const addToFavorites = async (userId, listingId) => {
  const data = await cachedFetch('/favorites', {}, {
    method: 'POST',
    body: { user_id: userId, listing_id: listingId },
    forceRefresh: true
  });
  
  // Invalidate favorites cache for this user
  apiCache.delete(apiCache.generateKey(`/favorites/${userId}`, {}));
  apiCache.clearByPattern('/listings');
  
  return data;
};

export const removeFromFavorites = async (userId, listingId) => {
  const data = await cachedFetch('/favorites', {}, {
    method: 'DELETE',
    body: { user_id: userId, listing_id: listingId },
    forceRefresh: true
  });
  
  // Invalidate favorites cache for this user
  apiCache.delete(apiCache.generateKey(`/favorites/${userId}`, {}));
  apiCache.clearByPattern('/listings');
  
  return data;
};

export const markNotificationAsRead = async (notificationId) => {
  const response = await fetch(`${BASE_URL}/notifications/${notificationId}/read`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({
    }),
  });
  return handleResponse(response);
};

export const markAllNotificationsAsRead = async () => {
  const response = await fetch(`${BASE_URL}/notifications/read-all`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({
    }),
  });
  return handleResponse(response);
};

export const deleteNotification = async (notificationId) => {
  const response = await fetch(`${BASE_URL}/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: { 
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
    }),
  });
  
  return handleResponse(response);
};

export const updateSellerProfile = async (userId, updateData) => {
  const data = await cachedFetch('/seller/update-profile', {}, {
    method: 'POST',
    body: { user_id: userId, ...updateData },
    forceRefresh: true
  });
  
  // Invalidate seller cache
  apiCache.delete(apiCache.generateKey(`/sellers/${userId}`, {}));
  apiCache.clearByPattern('/sellers/');
  
  return data;
};

export const setupSeller = async (sellerData) => {
  const response = await fetch(`${BASE_URL}/sellers/setup`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(sellerData)
  });
  return handleResponse(response);
};

export const uploadListingImages = async (listingId, images) => {
  const formData = new FormData();
  
  images.forEach((file, index) => {
    formData.append('images[]', file, file.name || `image_${index}.jpg`);
  });
  
  formData.append('listing_id', listingId.toString());

  const headers = getAuthHeaders();
  delete headers['Content-Type'];

  const response = await fetch(`${BASE_URL}/upload-listing-images`, {
    method: 'POST',
    body: formData,
    headers
  });

  const responseText = await response.text();
  
  if (!responseText) {
    throw new Error('Empty response from server');
  }
  
  let result;
  try {
    result = JSON.parse(responseText);

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(result.error || result.message || `Upload failed with status ${response.status}`);
    }
    
    return result;
  } catch (parseError) {
    throw new Error('Server returned invalid response');
  }
};

export const deleteListingImage = async (imageId) => {
  const response = await fetch(`${BASE_URL}/listing-images/${imageId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const createOrder = async (orderData) => {
  const data = await cachedFetch('/orders', {}, {
    method: 'POST',
    body: orderData,
    forceRefresh: true
  });
  
  // Invalidate orders cache
  if (orderData.buyer_id) {
    apiCache.delete(apiCache.generateKey(`/orders/buyer/${orderData.buyer_id}`, {}));
  }
  if (orderData.seller_id) {
    apiCache.delete(apiCache.generateKey(`/orders/seller/${orderData.seller_id}`, {}));
  }
  
  return data;
};

export const verifyAccountAndName = async (data) => {
  const response = await fetch(`${BASE_URL}/wallet/verify-account-name`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      account_number: data.account_number,
      bank_code: data.bank_code,
      account_name: data.account_name || ''
    })
  });
  
  return handleResponse(response);
};

export const addPayoutMethod = async (userId, payoutData) => {
  const response = await fetch(`${BASE_URL}/wallet/add-payout-method-verified`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      method_type: payoutData.method_type,
      details: payoutData.details,
      is_default: payoutData.is_default,
      verified_account_name: payoutData.verified_account_name
    })
  });
  return handleResponse(response);
};

export const deletePayoutMethod = async (userId, payoutMethodId) => {
  const response = await fetch(`${BASE_URL}/wallet/payout-methods/delete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      user_id: userId,
      payout_method_id: payoutMethodId 
    })
  });
  return handleResponse(response);
};

export const setDefaultPayoutMethod = async (userId, payoutMethodId) => {
  const response = await fetch(`${BASE_URL}/wallet/payout-methods/set-default`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      user_id: userId,
      payout_method_id: payoutMethodId 
    })
  });
  return handleResponse(response);
};

export const initializePayment = async (userId, email, amount, description = '') => {
  const response = await fetch(`${BASE_URL}/payment/initialize`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      email: email,
      amount: parseFloat(amount),
      description: description
    })
  });
  return handleResponse(response);
};

export const processWalletPayment = async (orderId, amount, userId) => {
  const response = await fetch(`${BASE_URL}/orders/${orderId}/wallet-payment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      amount: amount
    })
  });
  return handleResponse(response);
};

export const depositToWallet = async (userId, amount, description = '') => {
  const response = await fetch(`${BASE_URL}/wallet/deposit`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      user_id: userId,
      amount: parseFloat(amount),
      description 
    })
  });
  return handleResponse(response);
};

export const withdrawFromWallet = async (userId, amount, description = '') => {
  const response = await fetch(`${BASE_URL}/wallet/withdraw`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      user_id: userId,
      amount: parseFloat(amount),
      description 
    })
  });
  return handleResponse(response);
};

export const processWithdrawal = async (userId, payoutMethodId, amount, description = '') => {
  const response = await fetch(`${BASE_URL}/withdraw`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      payout_method_id: payoutMethodId,
      amount: parseFloat(amount),
      description: description
    })
  });
  return handleResponse(response);
};

export const verifyPaymentStatus = async (reference, userId) => {
  const response = await fetch(`${BASE_URL}/payment/status?reference=${reference}&user_id=${userId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

export const verifyWithdrawal = async (reference) => {
  const response = await fetch(`${BASE_URL}/withdrawal/verify`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reference })
  });
  return handleResponse(response);
};

export const getFeePreview = async (amount, transactionType) => {
  const response = await fetch(`${BASE_URL}/wallet/fee-preview`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      amount: parseFloat(amount),
      transaction_type: transactionType // 'deposit', 'withdrawal', 'purchase', 'boost'
    })
  });
  return handleResponse(response);
};

export const verifyWithdrawalOTP = async (transactionRef, otpCode, userId) => {
  const response = await fetch(`${BASE_URL}/payments/verify-withdrawal-otp`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      transaction_ref: transactionRef,
      otp_code: otpCode,
      user_id: userId
    })
  });
  return handleResponse(response);
};

export const resendWithdrawalOTP = async (transactionRef, userId) => {
  const response = await fetch(`${BASE_URL}/payments/resend-withdrawal-otp`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      transaction_ref: transactionRef,
      user_id: userId
    })
  });
  return handleResponse(response);
};

export const deleteListing = async (listingId) => {
  const data = await cachedFetch(`/my-listings/${listingId}`, {}, {
    method: 'DELETE',
    forceRefresh: true
  });
  
  // Invalidate listings cache
  apiCache.clearByPattern('/listings');
  apiCache.clearByPattern('/my-listings');
  
  return data;
};

export const updateListing = async (listingId, listingData) => {
  const response = await fetch(`${BASE_URL}/listings/${listingId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(listingData)
  });
  
  // Invalidate listings cache
  apiCache.clearByPattern('/listings');
  apiCache.clearByPattern('/my-listings');
  
  return handleResponse(response);
};

export const updateListingStatus = async (listingId, status) => {
  const data = await cachedFetch(`/my-listings/${listingId}/status`, {}, {
    method: 'PUT',
    body: { status },
    forceRefresh: true
  });
  
  // Invalidate listings cache
  apiCache.clearByPattern('/listings');
  apiCache.clearByPattern('/my-listings');
  
  return data;
};

export const createSupportTicket = async (ticketData) => {
  const response = await fetch(`${BASE_URL}/support/tickets`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(ticketData),
  });
  return handleResponse(response);
};

export const addSupportMessage = async (ticketId, messageData) => {
  const response = await fetch(`${BASE_URL}/support/tickets/${ticketId}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(messageData),
  });
  return handleResponse(response);
};

export const uploadSupportFile = async (formData) => {
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  
  const response = await fetch(`${BASE_URL}/support/upload-file`, {
    method: 'POST',
    headers: headers,
    body: formData,
  });
  return handleResponse(response);
};

export const updateTicketStatus = async (ticketId, statusData) => {
  const response = await fetch(`${BASE_URL}/support/tickets/${ticketId}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(statusData),
  });
  return handleResponse(response);
};

export const sendMessage = async (data) => {
  const response = await fetch(`${BASE_URL}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const markMessagesAsRead = async (conversationId, userId) => {
  const response = await fetch(`${BASE_URL}/messages/mark-read`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId,
      user_id: userId
    }),
  });
  return handleResponse(response);
};

export const startConversation = async (data) => {
  const response = await fetch(`${BASE_URL}/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const submitRating = async (orderId, ratingData) => {
  const response = await fetch(`${BASE_URL}/orders/${orderId}/rate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(ratingData),
  });
  
  return handleResponse(response);
};

export const initializeBoost = async (data) => {
  const response = await fetch(`${BASE_URL}/boost/initialize`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const verifyBoostPayment = async (reference) => {
  const response = await fetch(`${BASE_URL}/boost/verify-payment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reference }),
  });
  return handleResponse(response);
};

export const checkCanChangeBoost = async (orderId, userId) => {
  const response = await fetch(`${BASE_URL}/boost/order/${orderId}/can-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ user_id: userId }),
  });
  return handleResponse(response);
};

export const cancelBoostOrder = async (orderId, userId) => {
  const response = await fetch(`${BASE_URL}/boost/order/${orderId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ user_id: userId }),
  });
  return handleResponse(response);
};

export const trackImpression = async (trackingData) => {
  const response = await fetch(`${BASE_URL}/tracking/impression`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(trackingData),
  });
  return handleResponse(response);
};

export const trackEngagement = async (engagementData) => {
  const response = await fetch(`${BASE_URL}/tracking/engagement`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(engagementData),
  });
  return handleResponse(response);
};

export const trackBoostImpression = async (data) => {
  const response = await fetch(`${BASE_URL}/boost/track-impression`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const trackBoostEngagement = async (data) => {
  const response = await fetch(`${BASE_URL}/boost/track-engagement`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const getBoostPerformanceComparison = async (boostId) => {
  const response = await fetch(`${BASE_URL}/boost/performance/${boostId}/comparison`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const capturePreBoostMetrics = async (listingId, boostId) => {
  const response = await fetch(`${BASE_URL}/boost/capture-baseline`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ listing_id: listingId, boost_id: boostId }),
  });
  return handleResponse(response);
};

export const initializeWalletBoost = async (data) => {
  const response = await fetch(`${BASE_URL}/boost/initialize-wallet`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const processWalletBoostPayment = async (orderId, data) => {
  const response = await fetch(`${BASE_URL}/boost/process-wallet-payment/${orderId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const checkBoostWalletBalance = async (data) => {
  const response = await fetch(`${BASE_URL}/boost/check-wallet-balance`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const calculateEscrowFees = async (amount) => {
  const response = await fetch(`${BASE_URL}/escrow/calculate-fees`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ amount })
  });
  return handleResponse(response);
};

export const createChatEscrow = async (data) => {
  const response = await fetch(`${BASE_URL}/escrow/chat/create`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const processEscrowWalletPayment = async (escrowId, data) => {
  const response = await fetch(`${BASE_URL}/escrow/${escrowId}/wallet-payment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const initializeEscrowCardPayment = async (escrowId, data) => {
  const response = await fetch(`${BASE_URL}/escrow/${escrowId}/card-payment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const confirmEscrowReceipt = async (escrowId, data) => {
  const response = await fetch(`${BASE_URL}/escrow/${escrowId}/confirm-receipt`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const raiseEscrowDispute = async (escrowId, data) => {
  const response = await fetch(`${BASE_URL}/escrow/${escrowId}/raise-dispute`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const cancelEscrow = async (escrowId, data) => {
  const response = await fetch(`${BASE_URL}/escrow/${escrowId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const uploadDisputeEvidence = async (formData) => {
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  
  const response = await fetch(`${BASE_URL}/disputes/upload-evidence`, {
    method: 'POST',
    headers: headers,
    body: formData
  });
  return handleResponse(response);
};

export const addDisputeMessage = async (disputeId, data) => {
  const response = await fetch(`${BASE_URL}/disputes/${disputeId}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const uploadStoreImage = async (userId, file, imageType) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('user_id', userId.toString());
  formData.append('image_type', imageType);

  const headers = getAuthHeaders();
  delete headers['Content-Type'];

  const response = await fetch(`${BASE_URL}/sellers/upload-image`, {
    method: 'POST',
    body: formData,
    headers: headers,
  });

  return handleResponse(response);
};

export const updateBoostAnalytics = async (boostId) => {
  const response = await fetch(`${BASE_URL}/analytics/update-boost/${boostId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const updateAllAnalytics = async () => {
  const response = await fetch(`${BASE_URL}/analytics/update-all`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const testEndpoint = async () => {
  const response = await fetch(`${BASE_URL}/test`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// AUTHENTICATION HELPERS
// ============================================

export const verifyEmail = async (userId, otpCode) => {
  const response = await fetch(`${BASE_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, code: otpCode }),
  });
  return handleResponse(response);
};

export const resendVerificationEmail = async (userId, email) => {
  const response = await fetch(`${BASE_URL}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, email: email }),
  });
  return handleResponse(response);
};

export const forgotPassword = async (email) => {
  const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handleResponse(response);
};

export const verifyPasswordResetOTP = async (email, otpCode) => {
  const response = await fetch(`${BASE_URL}/auth/verify-password-reset-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email, otp_code: otpCode }),
  });
  return handleResponse(response);
};

export const resetPassword = async (email, otpCode, newPassword, confirmPassword) => {
  const response = await fetch(`${BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ 
      email: email,
      otp_code: otpCode,
      password: newPassword,
      confirm_password: confirmPassword
    }),
  });
  return handleResponse(response);
};

// ============================================
// CACHE MANAGEMENT
// ============================================

export const clearAllCache = () => {
  apiCache.clear();
  // console.log('🧹 All API cache cleared');
};

export const clearCacheByPattern = (pattern) => {
  const cleared = apiCache.clearByPattern(pattern);
  // console.log(`🧹 Cleared ${cleared} cache entries for pattern: ${pattern}`);
  return cleared;
};

export const getCacheStats = () => {
  return apiCache.getStats();
};

export const forceRefresh = async (endpoint, params = {}, options = {}) => {
  return cachedFetch(endpoint, params, { ...options, forceRefresh: true });
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default {
  // Cache management
  clearAllCache,
  clearCacheByPattern,
  getCacheStats,
  forceRefresh,
  
  // Cached functions
  getCategories,
  getListings,
  getListing,
  getListingRatings,
  getListingPerformance,
  getPerformanceComparison,
  getFavorites,
  checkIsFavorite,
  getUserNotifications,
  getUnreadCountNotifications,
  getUser,
  updateUser,
  getStates,
  getLgasByState,
  getLcdasByLga,
  getSellerProfile,
  getSellerStats,
  getSellerUnreadCount,
  getTotalUnreadCount,
  getUnreadCountsBySeller,
  getSellerAllRatings,
  getSellerAnalytics,
  getOrdersByBuyer,
  getOrdersBySeller,
  getOrder,
  getWalletBalance,
  getWalletDetails,
  getTransactionHistory,
  getTransactionById,
  getPayoutMethods,
  getNigerianBanks,
  getMyListings,
  getUserSupportTickets,
  getSupportTicket,
  getSupportCategories,
  getSupportStatistics,
  getSupportTicketsInvolved,
  getBoostPackages,
  getListingBoosts,
  getUserBoostHistory,
  getUserBoostPerformanceHistory,
  getBoostPerformance,
  getBoostStatus,
  getBoostTransactions,
  getConversations,
  getMessages,
  getDisputeDetails,
  getDisputeEvidence,
  getUserDisputes,
  getDisputeMessages,
  getDisputeCategories,
  getActiveConversationEscrow,
  getConversationEscrowHistory,
  getUserEscrowHistory,
  getEscrowStatus,
  getEscrowDetails,
  getTrackingAnalytics,
  
  // Non-cached functions (write operations)
  login,
  register,
  verifyEmail,
  resendVerificationEmail,
  forgotPassword,
  verifyPasswordResetOTP,
  resetPassword,
  createListing,
  addToFavorites,
  removeFromFavorites,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  updateSellerProfile,
  setupSeller,
  uploadListingImages,
  deleteListingImage,
  createOrder,
  verifyAccountAndName,
  addPayoutMethod,
  deletePayoutMethod,
  setDefaultPayoutMethod,
  initializePayment,
  processWalletPayment,
  depositToWallet,
  withdrawFromWallet,
  processWithdrawal,
  verifyPaymentStatus,
  verifyWithdrawal,
  verifyWithdrawalOTP,
  resendWithdrawalOTP,
  deleteListing,
  updateListing,
  updateListingStatus,
  createSupportTicket,
  addSupportMessage,
  uploadSupportFile,
  updateTicketStatus,
  sendMessage,
  markMessagesAsRead,
  startConversation,
  submitRating,
  reportListing,
  initializeBoost,
  verifyBoostPayment,
  checkCanChangeBoost,
  cancelBoostOrder,
  trackImpression,
  trackEngagement,
  trackBoostImpression,
  trackBoostEngagement,
  capturePreBoostMetrics,
  initializeWalletBoost,
  processWalletBoostPayment,
  checkBoostWalletBalance,
  calculateEscrowFees,
  createChatEscrow,
  processEscrowWalletPayment,
  initializeEscrowCardPayment,
  confirmEscrowReceipt,
  raiseEscrowDispute,
  cancelEscrow,
  uploadDisputeEvidence,
  addDisputeMessage,
  uploadStoreImage,
  updateBoostAnalytics,
  updateAllAnalytics,
  testEndpoint,
  
  // Constants
  BASE_URL,
  IMAGE_BASE_URL
};

export {
  BASE_URL,
  IMAGE_BASE_URL
};